import axios from 'axios';
import { getAuthToken } from '../auth/authSession';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// create instance (same pattern as shiftApi)
const bookingClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// attach token
bookingClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// error handler (same pattern)
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? error.message;
  }
  return 'Request failed';
};

// ===============================
// ✅ BOOK SHIFT
// ===============================
export const bookShift = async (shift_id: number) => {
  try {
    const { data } = await bookingClient.post<ApiResponse<null>>('/bookings', {
      shift_id
    });

    if (!data.success) {
      throw new Error(data.message || 'Failed to book shift');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// ===============================
// 🔜 (NEXT) GET MY BOOKINGS
// ===============================
export const getMyBookings = async () => {
  try {
    const { data } = await bookingClient.get('/bookings/my');

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch bookings');
    }

    return data.bookings;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// ===============================
// 🔜 (NEXT) CANCEL BOOKING
// ===============================
export const cancelBooking = async (id: number) => {
  try {
    const { data } = await bookingClient.patch(`/bookings/${id}/cancel`);

    if (!data.success) {
      throw new Error(data.message || 'Failed to cancel booking');
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getShiftApplicants = async (shiftId: number) => {
  try {
    const { data } = await bookingClient.get(
      `/bookings/shift/${shiftId}/applicants`
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch applicants');
    }

    return data.applicants;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getApplicantDetails = async (bookingId: number) => {
  try {
    const { data } = await bookingClient.get(`/bookings/${bookingId}/details`);

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch applicant details');
    }

    return data.applicant;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};
